<script lang="ts">
    import ReviewTextForm from "@apps/reviews/components/ReviewForm/ReviewTextForm.svelte";
    import { REVIEWER_NAME_DISPLAY_FORMAT } from "@apps/reviews/constants";
    import ReviewsStorefrontValuesSettingsEntity from "@apps/reviews/entities/ReviewsStorefrontValuesSettingsEntity";
    import FormManager from "@lib/FormManager/FormManager";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { MoneyModule } from "@modules/money/MoneyModule";
    import { map } from "rxjs";
    import { container } from "tsyringe";

    import { stepperControllerContextToken } from "@components/Stepper/context_tokens";
    import T from "@components/T.svelte";

    import { REVIEWS_FORM_STEPS_NAMES } from "../../../constants";
    import ReviewsWizardManager from "../../../managers/ReviewsWizardManager";
    import LeaveReviewEarningRuleQuery from "../../../queries/LeaveReviewEarningRuleQuery";
    import {
        getProductReviewSubTitleTranslation,
        getReviewFormConfig,
    } from "./helpers";

    export let isAuthenticated: boolean;
    export let leaveReviewEarningRuleQuery: LeaveReviewEarningRuleQuery;
    export let reviewsSettings: ReviewsStorefrontValuesSettingsEntity;
    export let reviewsWizardManager: ReviewsWizardManager;

    const stepperController = getTypedContext(stepperControllerContextToken);
    const stepList$ = stepperController.stepList$;

    const rewarding$ = leaveReviewEarningRuleQuery.leaveReviewEarningRule$.pipe(
        map((l) => l?.rewarding)
    );

    const moneyModule = container.resolve(MoneyModule);
    const moneyFormatter = moneyModule.exports.moneyFormatter;
    const i18nModule = container.resolve(I18nModule);

    const isLastFormStep$ = stepList$.pipe(
        map(
            (arr) =>
                arr.length >= 2 &&
                arr[arr.length - 2].name === REVIEWS_FORM_STEPS_NAMES.REVIEW
        )
    );

    const reviewFormSchemaWithInitialValues = getReviewFormConfig(
        reviewsSettings.isReviewTitleEnabled,
        reviewsSettings.isReviewTitleRequired,
        reviewsSettings.customerDisplayNamePolicy
    );

    const formManager = reviewsWizardManager.cacheManager.get(
        REVIEWS_FORM_STEPS_NAMES.REVIEW,
        [$isLastFormStep$],
        () => {
            return new FormManager({
                initialValues: reviewFormSchemaWithInitialValues.initialValues,
                unregisterOnUnmount: false,
                resolver: yupResolver(reviewFormSchemaWithInitialValues.schema),
                validationStrategy: ValidationStrategy.OnSubmit,
                onSubmit: ({ values, errors }) => {
                    if (!values || (errors && Object.values(errors).length > 0))
                        return;

                    reviewsWizardManager.collectFormData(values);
                    if ($isLastFormStep$) {
                        reviewsWizardManager.submitCollectedFormData();
                    } else {
                        stepperController.nextStep();
                    }
                },
            });
        }
    );

    const setNavButtons = () => {
        if ($isLastFormStep$) {
            reviewsWizardManager.setNextButton({
                type: "submit",
                isActive: true,
                onClick: () => {
                    formManager.submit();
                },
            });
        } else {
            reviewsWizardManager.setNextButton({
                type: "next",
                isActive: true,
                onClick: () => {
                    formManager.submit();
                },
            });
        }

        reviewsWizardManager.setPrevButton({
            type: "prev",
            isActive: true,
            onClick: () => {
                stepperController.prevStep();
            },
        });
    };

    $: {
        $isLastFormStep$;
        setNavButtons();
    }

    const t_points_name$ = i18nModule.exports.i18n.selectT(
        "common.critical.Points",
        "points_name_plural"
    );

    $: productReviewSubTitleTranslation = getProductReviewSubTitleTranslation(
        $rewarding$,
        $t_points_name$,
        moneyFormatter
    );
</script>

<div class="gw-rv-form-review">
    <div class="gw-rv-form-review__header">
        <h3 class="gw-rv-form-review__title">
            <T
                namespace="reviews.ReviewsFormModalWidget"
                translationKey="review_step_title"
            />
        </h3>
        <h4 class="gw-rv-form-review__subtitle">
            <T
                namespace="reviews.ReviewsFormModalWidget"
                translationKey={productReviewSubTitleTranslation.key}
                translationOptions={productReviewSubTitleTranslation.translationOptions}
            />
        </h4>
    </div>

    <div class="gw-rv-form-review__inputs">
        <ReviewTextForm
            {formManager}
            showTitleInput={reviewsSettings.isReviewTitleEnabled}
            {isAuthenticated}
            isCustomerNameChoiceEnabled={isAuthenticated &&
                reviewsSettings.customerDisplayNamePolicy ===
                    REVIEWER_NAME_DISPLAY_FORMAT.CUSTOMER_CHOICE}
        />
    </div>
</div>

<style lang="postcss">
    .gw-rv-form-review {
        width: 570px;

        @media screen and (max-width: 600px) {
            width: 100%;
        }

        &__title {
            font-size: 1.8em;
            line-height: 1.2;
            font-weight: 600;
            margin: 0;
            text-align: center;
        }

        &__subtitle {
            margin: 0.7em 0 0;
            font-size: 1.2em;
            font-style: normal;
            font-weight: 400;
            line-height: 1.8;
            text-align: center;
        }

        &__inputs {
            margin-top: 2em;
        }
    }
</style>
